import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AUTH0_CONFIG, REGION_TO_AWS } from 'ext/lib/auth/constants';
import type { AccountState } from 'ext/entities/account';

/**
 * Hook to get the studio URL from state.
 * Use this instead of directly accessing import.meta.env.STUDIO_URL
 * to support region-based URLs injected by the extension.
 *
 * The default value (import.meta.env.STUDIO_URL) is set at the injection point
 * in each builder's createInitialState function.
 *
 * @returns The studio URL from state
 */
export const useStudioUrl = (): string => {
  return useSelector(
    (state: { account: AccountState }) => state.account?.studioUrl ?? ''
  );
};

/**
 * Non-hook version for sagas/non-component code.
 * Gets the studio URL from state.
 *
 * The default value (import.meta.env.STUDIO_URL) is set at the injection point
 * in each builder's createInitialState function.
 *
 * @param state - Redux state with account slice
 * @returns The studio URL from state
 */
export const getStudioUrlFromState = (state: {
  account: AccountState;
}): string => {
  return state.account?.studioUrl ?? '';
};

/**
 * The kinds of experience the extension can link to a settings page for.
 */
export type SettingsLinkType =
  | 'banner'
  | 'embed'
  | 'flow'
  | 'launchpad'
  | 'nps'
  | 'pin'
  | 'legacy-flow';

/** Legacy Studio keeps a separate route per type. */
const LEGACY_STUDIO_PATH: Record<SettingsLinkType, string> = {
  banner: 'banners',
  embed: 'embeds',
  flow: 'flows-v2',
  launchpad: 'launchpads',
  nps: 'nps-v2',
  pin: 'pins',
  'legacy-flow': 'flows',
};

/**
 * Which types have a settings page in the new app. Mirrors its
 * `SETTINGS_CONFIG_BY_TYPE` registry (agent-studio,
 * `components/experience/settings/page-registry.ts`).
 *
 * Legacy flows are the sole exclusion: the new app knows them as `journey` and
 * has no page for them, so they keep deep-linking to Studio however the flag is
 * set. Adding a type here without a page on the far side sends customers to a
 * route that does not exist.
 */
const HAS_NEW_APP_SETTINGS: Record<SettingsLinkType, boolean> = {
  banner: true,
  embed: true,
  flow: true,
  launchpad: true,
  nps: true,
  pin: true,
  'legacy-flow': false,
};

/** Builds an experience's settings URL. Named so consumers can hold one. */
export type GetExperienceSettingsUrl = (
  type: SettingsLinkType,
  experienceId?: string
) => string;

/** Keyed by the AWS identifier `REGION_TO_AWS` writes into `account.region`. */
const AGENT_STUDIO_URL_BY_REGION: Record<string, string | undefined> = {
  [REGION_TO_AWS.US]: AUTH0_CONFIG.US.agentStudioUrl,
  [REGION_TO_AWS.EU]: AUTH0_CONFIG.EU.agentStudioUrl,
};

/**
 * Base URL of the new app for this account's region, or `undefined` when the
 * region is not conclusively known.
 *
 * Derived from `account.region` rather than plumbed through the `appcues:auth`
 * payload the way `studioUrl` is: `region` is already set on every bootstrap
 * path, so the extra plumbing would buy nothing. If the new app ever needs a
 * per-account URL rather than a per-region one, plumb it like `studioUrl` then.
 *
 * Deliberately no US default. `account` starts as `{}` and every builder's
 * `createInitialState` destructures `region` with no fallback, so `undefined` is
 * reachable — and defaulting it to US would hand an EU customer a US URL, where
 * their account does not exist. Unknown region means unknown, and callers fall
 * back to the legacy link that was already right for them.
 */
export const useAgentStudioUrl = (): string | undefined => {
  const region = useSelector(
    (state: { account: AccountState }) => state.account?.region
  );

  return region ? AGENT_STUDIO_URL_BY_REGION[region] : undefined;
};

/* The bases come from opscues/SSM, which nobody in this repo controls, so a
   trailing slash there is a live possibility and would otherwise emit `//`. */
const joinUrl = (base: string, path: string): string =>
  `${base.replace(/\/+$/, '')}/${path}`;

const originOf = (url?: string): string | undefined => {
  if (!url) return undefined;
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
};

/**
 * Origin the builder is running inside.
 *
 * Both apps load the builder by appending a script to their own document
 * (Studio's `StandaloneBuilder/lib/standalone.ts`, the new app's
 * `StandaloneBuilder.tsx`) rather than framing it, so this is the host app's
 * origin in standalone mode. In the extension the builder is injected into the
 * customer's own page, so it matches neither app.
 */
const currentOrigin = (): string | undefined =>
  typeof window === 'undefined' ? undefined : window.location?.origin;

/**
 * Builds the URL for an experience's settings page.
 *
 * Both apps have these settings pages today, so the first rule is to stay put:
 * when the builder is running inside one of them, its links point back at that
 * same app. A standalone session launched from Studio therefore keeps the user
 * in Studio, and one launched from the new app keeps them in the new app —
 * neither hands the user across apps mid-task.
 *
 * The `agent-studio` flag only decides the case where the builder is running
 * somewhere that is neither app, which in practice means the Chrome extension
 * injected into the customer's own site.
 *
 * Legacy flows are the exception to all of it: the new app knows them as
 * `journey` and has no page for them, so they resolve to Studio even when the
 * builder is running inside the new app.
 *
 * Falls back to the legacy URL whenever the new app's base URL is unknown — an
 * unrecognized region, or the opscues parameter missing for this environment.
 * That fallback is the safety story: it rules out a cross-region hand-off, and
 * it degrades a missing parameter to today's behaviour instead of linking to
 * `undefined/experiences/...`.
 *
 * Returns `''` for a missing id. That is falsy so a caller can guard on it, but
 * it is not self-evidently harmless: `window.open('')` opens an `about:blank`
 * tab, so the `open()` call sites check before navigating. As an `href` it would
 * resolve to the current document, which is why the two render-time consumers
 * only ever pass an id they already have.
 */
export const useExperienceSettingsUrl = (): GetExperienceSettingsUrl => {
  const studioUrl = useStudioUrl();
  const agentStudioUrl = useAgentStudioUrl();
  const { agentStudio } = useFlags();

  return useCallback(
    (type: SettingsLinkType, experienceId?: string): string => {
      if (!experienceId) return '';

      const legacyUrl = joinUrl(
        studioUrl,
        `${LEGACY_STUDIO_PATH[type]}/${experienceId}/settings`
      );

      if (!HAS_NEW_APP_SETTINGS[type] || !agentStudioUrl) return legacyUrl;

      const newAppUrl = joinUrl(
        agentStudioUrl,
        `experiences/${experienceId}/settings`
      );
      const origin = currentOrigin();

      if (origin && origin === originOf(studioUrl)) return legacyUrl;
      if (origin && origin === originOf(agentStudioUrl)) return newAppUrl;

      return agentStudio ? newAppUrl : legacyUrl;
    },
    [agentStudio, agentStudioUrl, studioUrl]
  );
};
